import React from 'react';
import {debounce} from 'ts-debounce';

import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import * as Keyboard from 'util/Keyboard';
import pickDefined from 'util/pickDefined';
import {InputChangeEvent} from 'util/Types';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

export const DEBOUNCE_MS = 250;
export interface TwoInputFilterFormProps extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  onChangeValueOne: (value: string) => void;
  onChangeValueTwo: (value: string) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  valueOnePlaceholder: string;
  valueTwoPlaceholder: string;
  initialValueOne: string;
  initialValueTwo: string;
}
interface State {
  valueOne: string;
  valueTwo: string;
}

export default class TwoInputFilterForm<T extends TwoInputFilterFormProps> extends React.Component<T, State> {
  constructor(props: T) {
    super(props);
    this.state = {
      valueOne: this.props.initialValueOne,
      valueTwo: this.props.initialValueTwo,
    };
  }

  _handleValueChangeOne = (value: string): void => {
    this.props.onChangeValueOne(value);
  };

  _handleValueChangeTwo = (value: string): void => {
    this.props.onChangeValueTwo(value);
  };

  handleChangeValueOneDebounced = debounce(this._handleValueChangeOne, DEBOUNCE_MS);
  handleChangeValueTwoDebounced = debounce(this._handleValueChangeTwo, DEBOUNCE_MS);

  handleChangeValueOne = (valueOne: string): void => {
    this.setState({valueOne});
    this.handleChangeValueOneDebounced(valueOne);
  };

  handleChangeValueTwo = (valueTwo: string): void => {
    this.setState({valueTwo});
    this.handleChangeValueTwoDebounced(valueTwo);
  };

  getTextInputs(): Array<JSX.Element> {
    const firstTextInput = (
      <TextInput
        onChange={(e: InputChangeEvent) => {
          this.handleChangeValueOne(e.target.value);
        }}
        placeholder={this.props.valueOnePlaceholder}
        size={Size.SMALL}
        defaultValue={this.state.valueOne}
      />
    );
    const secondTextInput = (
      <TextInput
        onChange={(e: InputChangeEvent) => {
          this.handleChangeValueTwo(e.target.value);
        }}
        onKeyDown={Keyboard.callOnEnter(this.props.onEnter)}
        placeholder={this.props.valueTwoPlaceholder}
        size={Size.SMALL}
        defaultValue={this.state.valueTwo}
      />
    );
    return [firstTextInput, secondTextInput];
  }

  getNodeGrid(): Array<Array<JSX.Element>> {
    const [firstTextInput, secondTextInput] = this.getTextInputs();
    return [[firstTextInput], [secondTextInput]];
  }

  override render(): JSX.Element {
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);

    if (this.props.filterStyle === FilterStyle.PRIMARY) {
      return (
        <PrimaryFilterFormSkeleton
          {...skeletonProps}
          content={<FilterFormSkeletonNodeLayout nodeGrid={this.getNodeGrid()} />}
          dropdownToggleText={this.props.dropdownToggleText}
        />
      );
    } else {
      if (!this.props.onRemove) {
        throw new Error('Expected this.props.onRemove to be present when filterStyle is SECONDARY');
      }
      return (
        <SecondaryFilterFormSkeleton
          {...skeletonProps}
          nodeGrid={this.getNodeGrid()}
          onChangeFilterKey={this.props.onChangeFilterKey}
          selectedFilterKey={this.props.selectedFilterKey}
          onRemove={this.props.onRemove}
        />
      );
    }
  }
}
